import React from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FundList from './FundList';
import ProvenanceTrail from './ProvenanceTrail';
import ContributionSplit from './ContributionSplit';

/**
 * RecommendationCard Component
 *
 * Displays the complete recommendation with:
 * - Risk profile summary
 * - Fund recommendations
 * - Contribution split (Pension/ISA/GIA)
 * - Allocation chart
 * - Provenance trail (audit trail)
 * - Regulatory compliance footer
 */

const RecommendationCard = ({ recommendation, profile }) => {

  // Convert SRRI to risk category label — matches admin panel 7-level mapping
  const getRiskCategoryLabel = (srri) => {
    const labels = { 1: 'Very Low', 2: 'Low', 3: 'Low-Medium', 4: 'Medium', 5: 'Medium-High', 6: 'High', 7: 'Very High' };
    return labels[srri] || 'Unknown';
  };

  // Convert normalised score (0-1) to risk category label — matches SRRI band boundaries
  const getScoreLabel = (score) => {
    if (score === null || score === undefined) return 'N/A';
    if (score <= 0.15) return 'Very Low';
    if (score <= 0.30) return 'Low';
    if (score <= 0.44) return 'Low-Medium';
    if (score <= 0.58) return 'Medium';
    if (score <= 0.72) return 'Medium-High';
    if (score <= 0.86) return 'High';
    return 'Very High';
  };

  const getRiskBadgeClass = (maxSrri) => {
    const classes = { 1: 'very-low', 2: 'low', 3: 'low-medium', 4: 'medium', 5: 'medium-high', 6: 'high', 7: 'very-high' };
    return classes[maxSrri] || 'unknown';
  };

  // Extract data from the API response structure
  const {
    risk_assessment,
    fund_recommendations,
    contribution_advice,
    wrapper_distribution,
    compliance_checks,
    overall_compliance_status,
    compliance_score,
    summary_explanation,
    detailed_reasoning,
    provenance_trail
  } = recommendation;

  const riskCategory = getRiskCategoryLabel(risk_assessment?.max_srri || 4);
  const toleranceLabel = getScoreLabel(risk_assessment?.risk_tolerance_score);
  const capacityLabel = getScoreLabel(risk_assessment?.risk_capacity_score);
  const wasConstrained = risk_assessment?.limiting_factor === 'capacity';

  return (
      <div>
        {/* Success Header */}
        <div className="recommendation-header d-flex align-items-center mb-4 p-4 bg-success bg-opacity-10 rounded">
          <div className="success-icon me-3">
            <FontAwesomeIcon icon="check-circle" size="2x" className="text-success" />
          </div>
          <div>
            <h2 className="mb-1">Your Recommendation</h2>
            <p className="mb-0 text-muted">Based on your profile, goals, and risk assessment</p>
          </div>
        </div>

        {/* Summary Explanation */}
        {summary_explanation && (
            <div className="mb-4 p-3 bg-light rounded">
              <FontAwesomeIcon icon="lightbulb" className="me-2 text-warning" />
              <span className="fw-semibold">Summary: </span>
              {summary_explanation}
            </div>
        )}

        {/* Constraint Alerts */}
        {wasConstrained && (
            <Alert variant="info" className="d-flex align-items-center">
              <FontAwesomeIcon icon="info-circle" className="me-2" />
              <div>
                <strong>Capacity adjustment applied:</strong> Your risk tolerance
                ({toleranceLabel}) exceeds your capacity for loss
                ({capacityLabel}).
                We've adjusted your recommendation to protect against losses you may not be able to absorb.
              </div>
            </Alert>
        )}

        {profile?.goals?.yearsToRetirement && profile.goals.yearsToRetirement <= 5 && (
            <Alert variant="warning" className="d-flex align-items-center">
              <FontAwesomeIcon icon="clock" className="me-2" />
              <div>
                <strong>Time horizon adjustment:</strong> With {profile.goals.yearsToRetirement} years
                until retirement, we've reduced risk exposure to protect your near-term outcomes.
              </div>
            </Alert>
        )}

        {/* Risk Profile Summary */}
        <Row className="mb-4">
          <Col md={4}>
            <div className="text-center p-3 bg-light rounded">
              <div className="text-muted small mb-1">Risk Tolerance</div>
              <div className="fw-bold fs-5">{toleranceLabel}</div>
              <div className="text-muted small">From questionnaire</div>
            </div>
          </Col>
          <Col md={4}>
            <div className="text-center p-3 bg-light rounded">
              <div className="text-muted small mb-1">Risk Capacity</div>
              <div className="fw-bold fs-5">{capacityLabel}</div>
              <div className="text-muted small">From financial situation</div>
            </div>
          </Col>
          <Col md={4}>
            <div className="text-center p-3 bg-primary text-white rounded">
              <div className="small mb-1 opacity-75">Final Risk Profile</div>
              <div className="fw-bold fs-5">{riskCategory}</div>
              <div className="small opacity-75">
                Max SRRI: {risk_assessment?.max_srri || 'N/A'}/7
                {risk_assessment?.limiting_factor && (
                    <span> (limited by {risk_assessment.limiting_factor})</span>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Risk Category Badge */}
        <div className="mb-4">
        <span className={`risk-badge ${getRiskBadgeClass(risk_assessment?.max_srri || 4)}`}>
          <FontAwesomeIcon icon="shield-alt" className="me-2" />
          Risk Profile: <strong>{riskCategory}</strong>
          <span className="mx-2">|</span>
          Max SRRI: {risk_assessment?.max_srri || 'N/A'}/7
        </span>
        </div>

        <Row>
          <Col lg={8}>
            {/* Fund Recommendations */}
            <h5 className="mb-3">
              <FontAwesomeIcon icon="chart-pie" className="me-2 text-primary" />
              Recommended Funds
            </h5>
            <FundList funds={fund_recommendations?.map(rec => ({
              name: rec.fund?.name || 'Unknown Fund',
              provider: rec.fund?.provider || 'Fund Provider',
              risk: rec.fund?.srri || 0,
              allocation: rec.allocation_percentage || 0,
              charge: rec.fund?.ter || 0,
              suitabilityScore: rec.suitability_score || 0,
              rationale: rec.selection_rationale || '',
              returns: {
                oneYear: rec.fund?.performance_1yr || 0,
                threeYear: rec.fund?.performance_3yr || 0,
                fiveYear: rec.fund?.performance_5yr || 0
              }
            })) || []} />

            {/* Contribution Split (Pension/ISA/GIA) - NEW */}
            {wrapper_distribution && (
                <ContributionSplit distributionResult={wrapper_distribution} profile={profile} />
            )}

            {/* Legacy Contribution Advice (fallback if no wrapper_distribution) */}
            {!wrapper_distribution && contribution_advice && (
                <div className="mt-4 p-3 bg-light rounded">
                  <h6 className="mb-3">
                    <FontAwesomeIcon icon="pound-sign" className="me-2 text-success" />
                    Contribution Recommendation
                  </h6>
                  <Row>
                    <Col sm={4}>
                      <div className="text-center">
                        <div className="fs-4 fw-bold text-success">
                          £{contribution_advice.monthly_amount?.toFixed(0)}
                        </div>
                        <div className="small text-muted">Monthly</div>
                      </div>
                    </Col>
                    <Col sm={4}>
                      <div className="text-center">
                        <div className="fs-5 fw-bold">
                          £{contribution_advice.tax_relief_amount?.toFixed(0)}
                        </div>
                        <div className="small text-muted">Annual Tax Relief</div>
                      </div>
                    </Col>
                    <Col sm={4}>
                      <div className="text-center">
                        <div className="fs-5 fw-bold">
                          {contribution_advice.affordability_percentage?.toFixed(1)}%
                        </div>
                        <div className="small text-muted">Of Net Income</div>
                      </div>
                    </Col>
                  </Row>
                  {contribution_advice.contribution_rationale && (
                      <div className="mt-3 small text-muted">
                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                        {contribution_advice.contribution_rationale}
                      </div>
                  )}
                </div>
            )}
          </Col>

          <Col lg={4}>
            {/* Compliance Status */}
            <h5 className="mb-3">
              <FontAwesomeIcon icon="percentage" className="me-2 text-primary" />
              Compliance Status
            </h5>
            <div className="p-3 bg-light rounded">
              <div className="text-center mb-3">
                <div className={`fs-4 fw-bold ${
                    overall_compliance_status === 'COMPLIANT' ? 'text-success' :
                        overall_compliance_status === 'COMPLIANT_WITH_WARNINGS' ? 'text-warning' :
                        overall_compliance_status === 'WARNING' ? 'text-warning' : 'text-danger'
                }`}>
                  {overall_compliance_status === 'COMPLIANT_WITH_WARNINGS' 
                    ? 'COMPLIANT ⚠' 
                    : overall_compliance_status || 'N/A'}
                </div>
                <div className="small text-muted">
                  Score: {((compliance_score || 0) * 100).toFixed(0)}%
                </div>
              </div>
              <div className="small">
                {compliance_checks?.map((check, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-truncate" style={{ maxWidth: '70%' }}>
                    {check.regulation}
                  </span>
                      <span className={`badge ${
                          check.status === 'PASS' ? 'bg-success' :
                              check.status === 'WARNING' ? 'bg-warning' : 'bg-danger'
                      }`}>
                    {check.status}
                  </span>
                    </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        {/* Detailed Reasoning */}
        {detailed_reasoning && (
            <div className="mt-4 p-3 border rounded">
              <h6 className="mb-2">
                <FontAwesomeIcon icon="file-alt" className="me-2 text-primary" />
                How the fund is chosen
              </h6>
              <p className="mb-0 text-muted small">{detailed_reasoning}</p>
            </div>
        )}

        {/* Provenance Trail */}
        <ProvenanceTrail provenanceTrail={provenance_trail} />

        {/* Regulatory Footer */}
        <div className="mt-4 p-3 bg-light rounded border">
          <div className="d-flex align-items-start">
            <FontAwesomeIcon icon="balance-scale" className="text-muted me-3 mt-1" />
            <div className="small text-muted">
              <strong>Regulatory Compliance</strong><br />
              This recommendation has been generated in accordance with FCA COBS 9.2.1R (suitability)
              and COBS 9.2.2R (information gathering requirements). A full audit trail with regulatory
              citations is available above. This is a POC demonstration and does not constitute
              financial advice.
            </div>
          </div>
        </div>
      </div>
  );
};

export default RecommendationCard;